import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import BasePageUAM from '../BasePageUAM/BasePageUAM';
import LoginPanel from './Panels/LoginPanel/LoginPanel';
import AlreadyLoggedInPanel from './Panels/AlreadyLoggedInPanel/AlreadyLoggedInPanel';
import { composeDestinationQuerystring, getReturnUrlByDestination } from '../../../shared-with-server/destination-utils';
import { destinationInfo } from '../../destructured-front-end-config';
import { isIdTokenValid } from '../../utils/jwt-utils';
import { getURLParamByName } from '../../../shared-with-server/product-name-utils';
import UAMLoginRegistrationModal from '../../components/modals/UAMLoginRegistrationModal/UAMLoginRegistrationModal';

const LoginUAMPage = ({ brand, destPath }) => {
  const redirectToDestPath = () => {
    window.location.href = getReturnUrlByDestination(destinationInfo, destPath.dest, destPath.path) || destPath.retURL; // Redirect to home
  };

  const includeGoogleSignIn = useRef(getURLParamByName(location?.search, 'googleSignIn')?.toLowerCase() === 'true');
  const isAddingAccount = getURLParamByName(location?.search, 'addAccount')?.toLowerCase() === 'true';
  const [validIdToken, setValidIdToken] = useState(isIdTokenValid());
  const [submitData, setSubmitData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [registrationStepPending, setRegistrationStepPending] = useState(null); // indicates which step to redirect to following a successful login with registration data remaining

  const onSignOut = () => {
    axios.delete('/api/v2/token')
      .then((res) => {
        setSubmitData(null);

        if (res.status === 200) {
          setValidIdToken(false);
        } else {
          setErrorMessage(`Unexpected error at logout (${res.status})`);
        }
      }).catch((err) => {
        setSubmitData(null);
        setErrorMessage(err?.response?.data?.message || 'Unknown error at logout');
      });
  };

  const getPanel = () => {
    if (validIdToken && !isAddingAccount) {
      return (
        <AlreadyLoggedInPanel
          onBack={redirectToDestPath}
          onSignOut={onSignOut}
        />
      );
    }

    if (registrationStepPending) {
      return (
        <UAMLoginRegistrationModal
          continueClickedFn={() => {
            window.location.href = `/uam/step${registrationStepPending}?${composeDestinationQuerystring(destinationInfo, destPath)}`;
          }}
        />
      );
    }

    return (
      <LoginPanel
        destPath={destPath}
        errorMessage={errorMessage}
        setSubmitData={setSubmitData}
        includeGoogleSignIn={includeGoogleSignIn.current}
        initialEmail={getURLParamByName(location?.search, 'email')}
      />
    );
  };

  useEffect(() => {
    if (!submitData) { return; }

    axios.post(
      '/api/v2/fi/token',
      {
        ...submitData,
        addAccount: isAddingAccount,
      },
      {
        validateStatus: (status) => status < 500 || status >= 600,
      },
    ).then((res) => {
      if (res.data?.subStatus === 'registrationRequired') {
        setRegistrationStepPending(res.data.step);
        return;
      }

      if (res.data?.message) {
        setErrorMessage(res.data.message);
      } else if (res.status === 200) {
        redirectToDestPath();
      } else {
        throw new Error('Unknown error at login');
      }
    }).catch((err) => {
      setErrorMessage(err.message || 'Unknown error at login');
    });
  }, [submitData]);

  return (
    <BasePageUAM
      activeTab={1}
      brand={brand}
      destPath={destPath}
      needsDestPath
    >
      {getPanel()}
    </BasePageUAM>
  );
};

LoginUAMPage.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.object.isRequired,
};

export default LoginUAMPage;
